import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import { IoMdClose } from 'react-icons/io';
import { RiArrowDropDownLine } from 'react-icons/ri';
import styled from 'styled-components';
import colors from '../res/styles/Colors';

MultiSelectWithTags.propTypes = {
    options: PropTypes.array,
    selectedValues: PropTypes.array,
    onChange: PropTypes.func,
    placeholder: PropTypes.string
};

MultiSelectWithTags.defaultProps = {
    options: [],
    selectedValues: [],
    onChange: () => { },
    placeholder: 'Select or digit a desease'
};

export default function MultiSelectWithTags({ options, selectedValues, onChange, placeholder }) {
    const [inputValue, setInputValue] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState([]);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    // Filter options based on input and exclude already selected values
    useEffect(() => {
        const lowerInput = inputValue.toLowerCase().trim();
        const filtered = options.filter(
            (opt) =>
                opt.toLowerCase().includes(lowerInput) &&
                !selectedValues.includes(opt)
        );
        setFilteredOptions(filtered);
    }, [inputValue, options, selectedValues]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
        setIsDropdownOpen(true);
    };

    const handleInputFocus = () => {
        setIsDropdownOpen(true);
    };

    const handleSelectOption = (option) => {
        if (!selectedValues.includes(option)) {
            onChange([...selectedValues, option]);
        }
        setInputValue('');
        setIsDropdownOpen(false);
        inputRef.current?.focus();
    };

    const handleCreateNew = () => {
        const trimmedValue = inputValue.trim();
        if (trimmedValue && !selectedValues.includes(trimmedValue)) {
            onChange([...selectedValues, trimmedValue]);
        }
        setInputValue('');
        setIsDropdownOpen(false);
    };

    const handleRemoveTag = (valueToRemove) => {
        onChange(selectedValues.filter((v) => v !== valueToRemove));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const trimmedValue = inputValue.trim();

            // If there's an exact match in filtered options, select it
            const exactMatch = filteredOptions.find(
                (opt) => opt.toLowerCase() === trimmedValue.toLowerCase()
            );

            if (exactMatch) {
                handleSelectOption(exactMatch);
            } else if (trimmedValue) {
                // Create new option
                handleCreateNew();
            }
        } else if (e.key === 'Escape') {
            setIsDropdownOpen(false);
        }
    };

    const showCreateOption = inputValue.trim() &&
        !options.some((opt) => opt.toLowerCase() === inputValue.trim().toLowerCase()) &&
        !selectedValues.some((v) => v.toLowerCase() === inputValue.trim().toLowerCase());

    return (
        <Container ref={containerRef}>
            <InputContainer>
                <StyledInput
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    aria-label={placeholder}
                />
                <DropdownArrow onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                    <RiArrowDropDownLine />
                </DropdownArrow>
            </InputContainer>

            {isDropdownOpen && (filteredOptions.length > 0 || showCreateOption) && (
                <DropdownList>
                    {showCreateOption && (
                        <CreateOption onClick={handleCreateNew}>
                            + Create &quot;{inputValue.trim()}&quot;
                        </CreateOption>
                    )}
                    {filteredOptions.map((option, index) => (
                        <DropdownItem
                            key={index}
                            onClick={() => handleSelectOption(option)}
                        >
                            {option}
                        </DropdownItem>
                    ))}
                </DropdownList>
            )}

            {selectedValues.length > 0 && (
                <TagsContainer>
                    {selectedValues.map((value, index) => (
                        <Tag key={index}>
                            <TagText>{value}</TagText>
                            <RemoveButton onClick={() => handleRemoveTag(value)}>
                                <IoMdClose />
                            </RemoveButton>
                        </Tag>
                    ))}
                </TagsContainer>
            )}
        </Container>
    );
}

const Container = styled.div`
    position: relative;
    width: 200px;
    margin-bottom: 10px;
`;

const InputContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const StyledInput = styled.input`
    height: 30px;
    width: 100%;
    border-radius: 5px;
    box-shadow: 0 1px 2px grey;
    border: solid 1px #ccc;
    padding-left: 10px;
    padding-right: 25px;

    &:focus {
        outline: none;
    }

    @media (max-width: 480px) {
        font-size: 16px;
    }
`;

const DropdownArrow = styled.span`
    position: absolute;
    right: -2px;
    color: #000;
    cursor: pointer;
    user-select: none;
    display: flex;
    align-items: center;

    svg {
        width: 26px;
        height: 26px;
    }
`;

const DropdownList = styled.div`
    position: absolute;
    top: 32px;
    left: 0;
    right: 0;
    max-height: 200px;
    overflow-y: auto;
    background: white;
    border-radius: 5px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 1000;
`;

const DropdownItem = styled.div`
    padding: 8px 10px;
    cursor: pointer;
    font-size: 14px;

    &:hover {
        background-color: #f0f0f0;
    }

    @media (max-width: 480px) {
        padding: 12px 10px;
    }
`;

const CreateOption = styled(DropdownItem)`
    color: ${colors.green};
    font-weight: 500;
    border-bottom: 1px solid #eee;

    &:hover {
        background-color: #e8f5e9;
    }
`;

const TagsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
`;

const Tag = styled.div`
    display: inline-flex;
    align-items: center;
    background-color: ${colors.green};
    color: white;
    padding: 6px 12px;
    border-radius: 8px;
    font-size: 13px;
    max-height: 30px;
`;

const TagText = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;

    @media (max-width: 480px) {
        max-width: 100px;
    }
`;

const RemoveButton = styled.button`
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 0;
    margin-left: 6px;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 14px;
        height: 14px;
    }

    &:hover {
        opacity: 0.8;
    }

    @media (max-width: 480px) {
        padding: 4px;
    }
`;

